import { useMemo, useState } from 'react'
import {
  ladderRunsUphill,
  loadDefaults,
  NOT_HERE,
  saveDefaults,
  shippedDefaults,
  tierProblems,
  type ShopDefaults,
} from '../lib/defaults'
import { loadDepositPref, saveDepositPref, type DepositPref } from '../lib/depositPref'
import { MONTHS, type DepositMode, type DueOn } from '../lib/paper'
import { NotKnown, SaidLine, useSaid } from './bits'

/**
 * HOW THIS SHOP WORKS, SET ONCE.
 *
 * SET-02. Three things were hardcoded into every quote this product has ever
 * written: one company's discount months, one company's percentages and one
 * company's terms paragraphs. A shop with its own had to retype them on all
 * forty houses in a season, which in practice means it stopped doing it by
 * about house six.
 *
 * A DRAFT AND A SAVE BUTTON, like the business profile beside it and for the
 * same reason: a half typed percentage is a percentage that goes out on a
 * quote, and there has to be something to press that says no, wait.
 *
 * THE SAFEGUARD IS ON THE SCREEN, NOT JUST IN THE CODE. Defaults apply to new
 * jobs and never to a quote already written, and that sentence is printed
 * where somebody about to change their terms in November can read it, because
 * the fear it answers is the reason they would not touch this screen at all.
 *
 * NOT A GATE. lib/quote.ts is explicit that nobody should have to open a
 * settings screen before they can write a price, and nothing here asks for
 * one. What this screen has no record for is named at the bottom rather than
 * left as an empty section.
 */
export default function Settings({ chromeless }: { chromeless?: boolean }) {
  const [saved, setSaved] = useState<ShopDefaults>(() => loadDefaults())
  const [draft, setDraft] = useState<ShopDefaults>(() => loadDefaults())
  const [savedDep, setSavedDep] = useState<DepositPref>(() => loadDepositPref())
  const [dep, setDep] = useState<DepositPref>(() => loadDepositPref())
  const { said, say, clear } = useSaid()

  const set = (p: Partial<ShopDefaults>) => {
    setDraft((d) => ({ ...d, ...p }))
    clear()
  }

  const problems = tierProblems(draft.tiers)
  const uphill = ladderRunsUphill(draft.tiers)
  const dirty = useMemo(
    () =>
      JSON.stringify(draft) !== JSON.stringify(saved) ||
      JSON.stringify(dep) !== JSON.stringify(savedDep),
    [draft, saved, dep, savedDep],
  )

  const save = () => {
    if (problems.length) return
    saveDefaults(draft)
    saveDepositPref(dep)
    setSaved(draft)
    setSavedDep(dep)
    say('Saved. Every job you start from now opens this way.')
  }

  const setTier = (i: number, p: { month?: number; offPct?: number }) =>
    set({ tiers: draft.tiers.map((t, n) => (n === i ? { ...t, ...p } : t)) })

  const addTier = () => {
    // The next month nobody has used yet, so a new rung is never born invalid.
    const used = new Set(draft.tiers.map((t) => t.month))
    let m = 8
    for (let i = 0; i < 12; i++) if (!used.has((8 + i) % 12)) { m = (8 + i) % 12; break }
    set({ tiers: [...draft.tiers, { month: m, offPct: 0 }] })
  }

  return (
    <>
      {/* One head for the whole of Settings; the tab strip above says which
          part of it this is. */}
      {!chromeless && (
        <div className="ws-head">
          <div>
            <h1>Settings</h1>
            <p>
              How your quotes open. Set once here, and every job you start after it works this
              way.
            </p>
          </div>
        </div>
      )}


      {/*
        A READING WIDTH. Every card on here holds a form or a paragraph, and
        both of those are read rather than scanned: a terms paragraph set
        1,600 pixels across is a line nobody can track back to the start of.
        The list pages take the full width because a table needs it. This
        does not.
      */}
      <div className="setpage">
      {/*
        THE SENTENCE THAT MAKES THIS SCREEN SAFE TO TOUCH. Somebody changing
        their terms in November is asking one question, and it is not about
        percentages.
      */}
      <p className="est-answer set-safe">
        <b>Nothing here reaches a quote you have already written.</b> These are the settings a
        new job starts from. A quote somebody has already been shown keeps exactly the terms,
        months and wording it was shown with.
      </p>

      {/* ---------------------------------------------------- the ladder --- */}

      <div className="ws-sec-head">
        <h2>The seasonal ladder</h2>
        <p>
          Install early, pay less. Most shops in this trade run one and they do not all run the
          same months, so these are yours. Leave it empty and every quote is list price.
        </p>
      </div>

      <div className="ws-card pad">
        {/*
          THE COLUMNS ARE NAMED ONCE, not on every rung. Three rows each
          carrying its own Install month and Off the list price was six labels
          for two facts, and the labels were taller than the controls. The
          screen readers keep theirs on the controls themselves.
        */}
        <div className="ladder">
          {draft.tiers.length === 0 ? (
            <p className="hint">No rungs. Every quote opens at list price.</p>
          ) : (
            <div className="ladder-head" aria-hidden="true">
              <span>Install month</span>
              <span>Off the list price</span>
              <span />
            </div>
          )}
          {draft.tiers.map((t, i) => (
            <div key={i} className="ladder-row">
              <select
                aria-label={`Rung ${i + 1}, install month`}
                value={t.month}
                onChange={(e) => setTier(i, { month: Number(e.target.value) })}
              >
                {MONTHS.map((m, n) => (
                  <option key={m} value={n}>
                    {m}
                  </option>
                ))}
              </select>
              <div className="pct">
                <input
                  aria-label={`Rung ${i + 1}, percent off the list price`}
                  type="number"
                  min={0}
                  max={100}
                  value={t.offPct}
                  onChange={(e) => setTier(i, { offPct: Number(e.target.value) })}
                />
                <em>%</em>
              </div>
              <button
                className="quiet-drop"
                aria-label={`Remove the ${MONTHS[t.month]} rung`}
                onClick={() => set({ tiers: draft.tiers.filter((_, n) => n !== i) })}
              >
                Remove
              </button>
            </div>
          ))}
          <button className="btn" onClick={addTier}>
            Add a rung
          </button>
        </div>

        {problems.map((p) => (
          <p key={p} className="set-bad">
            {p}
          </p>
        ))}
        {/*
          ALLOWED, AND SAID OUT LOUD. A ladder that discounts more the later
          you install is unusual and somebody may mean it, so it is a sentence
          rather than a block.
        */}
        {uphill && problems.length === 0 && (
          <p className="hint">
            This ladder discounts more the later the install. That is the opposite of the usual
            argument for booking early. Left alone in case you mean it.
          </p>
        )}

        <label className="set-check">
          <input
            type="checkbox"
            checked={draft.showLadder}
            onChange={(e) => set({ showLadder: e.target.checked })}
          />
          <span>Print the months on the quote, so waiting has a price</span>
        </label>

        <div className="ws-form set-form">
          <label className="ws-wide">
            <span>What the discount is called on the page</span>
            <input
              value={draft.discountLabel}
              placeholder="Leave empty to name it after the month"
              onChange={(e) => set({ discountLabel: e.target.value })}
            />
          </label>
        </div>
      </div>

      {/* ------------------------------------------------------- the money --- */}

      <div className="ws-sec-head">
        <h2>What you ask for, and when</h2>
        <p>The deposit a new quote opens with, and when the rest falls due.</p>
      </div>

      <div className="ws-card pad">
        <div className="ws-form set-form">
          <label>
            <span>Deposit</span>
            <select
              value={dep.mode}
              onChange={(e) => {
                setDep({ ...dep, mode: e.target.value as DepositMode })
                clear()
              }}
            >
              <option value="none">Nothing up front</option>
              <option value="percent">A percentage</option>
              <option value="flat">A fixed amount</option>
            </select>
          </label>
          {dep.mode !== 'none' && (
            <label>
              <span>{dep.mode === 'percent' ? 'How much of the total' : 'How much'}</span>
              <div className="pct">
                {dep.mode === 'flat' && <em className="pre">$</em>}
                <input
                  type="number"
                  min={0}
                  value={dep.value}
                  onChange={(e) => {
                    setDep({ ...dep, value: Number(e.target.value) })
                    clear()
                  }}
                />
                {dep.mode === 'percent' && <em>%</em>}
              </div>
            </label>
          )}
          <label className="ws-wide">
            <span>The balance is due</span>
            <select value={draft.dueOn} onChange={(e) => set({ dueOn: e.target.value as DueOn })}>
              <option value="completion">The day the install is finished</option>
              <option value="date">On a date I name on each quote</option>
            </select>
            {/*
              Worth saying here rather than being discovered on the invoices
              list: an invoice due on completion cannot be late, because
              nothing in this product records a job as finished yet.
            */}
            <small className="hint">
              {draft.dueOn === 'completion'
                ? 'Marking a stop finished on Today records the day. The balance is not flagged late on its own, because nobody has set how many days after that counts as late.'
                : 'You name the date on the quote itself. An invoice with no date on it is called out on the invoices list.'}
            </small>
          </label>
        </div>
      </div>

      {/* ------------------------------------------------------- the words --- */}

      <div className="ws-sec-head">
        <h2>Your terms</h2>
        <p>The two paragraphs beside the price on every quote and invoice.</p>
      </div>

      <div className="ws-card pad">
        <div className="ws-form set-form">
          <label className="ws-wide">
            <span>Left paragraph</span>
            <textarea
              rows={3}
              value={draft.termsLeft}
              onChange={(e) => set({ termsLeft: e.target.value })}
            />
          </label>
          <label className="ws-wide">
            <span>Right paragraph</span>
            <textarea
              rows={3}
              value={draft.termsRight}
              onChange={(e) => set({ termsRight: e.target.value })}
            />
          </label>
        </div>
      </div>

      <div className="ws-card pad set-save">
        <div className="ws-actions">
          {/*
            QUIETER THAN SAVE, and on the other side of the row. It was a
            second full button beside a disabled one, which made undoing the
            screen louder than using it.
          */}
          <button
            className="set-reset"
            onClick={() => {
              set(shippedDefaults())
              clear()
            }}
          >
            Back to how it shipped
          </button>
          <button className="btn primary" onClick={save} disabled={!dirty || problems.length > 0}>
            Save
          </button>
          <SaidLine said={said} dirty={dirty} />

        </div>
      </div>

      </div>

      <NotKnown
        title="What is not on this screen"
        items={[
          ...NOT_HERE,
          'Letterhead and your subscription. Both are under Money and Materials.',
        ]}
      />
    </>
  )
}
